import { FilterOutlined } from "@ant-design/icons";
import { Dropdown, Form, Input, Select } from "antd";
import { useState } from "react";
import styled from "styled-components";

const StyledDropdown = styled(Dropdown)`
  .rc-virtual-list-holder {
    max-height: 320px !important;
  }
`;

const StyledSearch = styled(Input.Search)`
  width: 500px;

  & .ant-input-affix-wrapper {
    border-radius: 0 !important;
  }
`;

const FilterToggle = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  padding: 0 4px;
  color: rgba(0, 0, 0, 0.45);

  &:hover {
    color: rgba(0, 0, 0, 0.88);
  }
`;

export default function SearchWithDropdown({
  initialValues = { query: "" },
  searchHook,
  searchResultGetter = (searchResult) => ({
    label: searchResult,
  }),
  inputProps = {},
  selectProps = {},
  collapsibleSelect = false,
}) {
  const [form] = Form.useForm();
  const [isOpen, setOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [request, setRequest] = useState();
  const [selectVisible, setSelectVisible] = useState(false);
  const [onSearch, { data: searchResults, isLoading, isFetching }] = searchHook();

  const onFinish = async (values) => {
    const { query, queryFieldType } = values;
    if (query && query.length > 0) {
      // Abort previous request
      if (request) {
        // @ts-ignore
        request.abort();
      }
      const req = onSearch({ query, queryFieldType });
      setRequest(req);
      await req;
      // open dropdown on search
      setOpen(true);
      // search in progress, so we are not initial anymore
      setIsInitialLoad(false);
    }
  };

  const items = (() => {
    // During initial load
    if (isInitialLoad) {
      return [
        {
          label: "Loading...",
        },
      ];
    }

    if (searchResults?.length <= 0) {
      return [
        {
          label: "No results found",
          disabled: isFetching,
        },
      ];
    }

    return (
      searchResults?.map(searchResultGetter).map((searchResult, index) => ({
        key: searchResult.key ?? `result-${index}`,
        label: searchResult.label,
        disabled: isFetching,
      })) ?? []
    );
  })();

  const fieldTypeSelect = selectProps?.options ? (
    <Form.Item noStyle name="queryFieldType">
      <Select
        popupMatchSelectWidth={false}
        allowClear
        placeholder="Select one..."
        onChange={() => {
          form.setFieldsValue({ query: initialValues?.query });
          setIsInitialLoad(true);
        }}
        onDropdownVisibleChange={(open) => {
          if (open) {
            setOpen(false);
          }
        }}
        {...selectProps}
      />
    </Form.Item>
  ) : null;

  const addonBefore = (() => {
    if (!fieldTypeSelect) return undefined;
    if (!collapsibleSelect) return fieldTypeSelect;
    if (!selectVisible) {
      return (
        <FilterToggle onClick={() => setSelectVisible(true)}>
          <FilterOutlined />
        </FilterToggle>
      );
    }
    return (
      <>
        {fieldTypeSelect}
        <FilterToggle
          onClick={() => {
            setSelectVisible(false);
            setOpen(false);
            form.resetFields(["queryFieldType"]);
            setIsInitialLoad(true);
          }}
        >
          <FilterOutlined />
        </FilterToggle>
      </>
    );
  })();

  return (
    <Form form={form} onFinish={onFinish} initialValues={initialValues}>
      <StyledDropdown
        menu={{
          items,
        }}
        trigger={["click"]}
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) setOpen(false);
        }}
      >
        <Form.Item noStyle name="query">
          <StyledSearch
            data-testid="search-input"
            addonBefore={addonBefore}
            onChange={(e) => {
              if (e.target.value.length > 0) {
                // @ToDo: uncomment when we confident enough
                // form.submit();
                // setOpen(true);
              } else {
                // if searchquery is empty, we start over
                setIsInitialLoad(true);
                setOpen(false);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                // close dropdown on escape
                setOpen(false);
              }
              if (e.key === "Enter") {
                // prevent second form submission
                e.preventDefault();
              }
            }}
            loading={isLoading || isFetching}
            onSearch={form.submit}
            allowClear
            enterButton
            {...inputProps}
          />
        </Form.Item>
      </StyledDropdown>
    </Form>
  );
}
